import { respond, type Message } from '../core/ai'
import { append, clear, h } from '../core/dom'
import { haptic } from '../core/haptics'
import { getSettings } from '../core/store'
import { t } from '../i18n'
import { topbar } from './shared'

/** Chat history lives for the session only. Nobody wants last night's 3am
 *  transcript waiting for them tomorrow evening. */
let history: Message[] = []

export function renderCompanion(root: HTMLElement, signal: AbortSignal): void {
  const d = t()
  const offline = !getSettings().aiEndpoint.trim()

  const chat = h('div', { class: 'chat' })
  const input = h('textarea', {
    rows: 1,
    placeholder: d.companion.placeholder,
    oninput: (e: Event) => {
      const el = e.target as HTMLTextAreaElement
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 130)}px`
      send.disabled = !el.value.trim()
    },
    onkeydown: (e: KeyboardEvent) => {
      // Enter sends on a physical keyboard; on touch the button is the only way,
      // because Enter should insert a newline when you are typing with thumbs.
      if (e.key === 'Enter' && !e.shiftKey && !('ontouchstart' in window)) {
        e.preventDefault()
        void submit()
      }
    },
  })

  const send = h(
    'button',
    { class: 'send', disabled: true, 'aria-label': 'Send', onclick: () => void submit() },
    '↑',
  )

  const bubble = (m: Message): HTMLElement =>
    h('div', { class: `bubble ${m.role}${m.safety ? ' safety' : ''}` }, m.text)

  const repaint = (): void => {
    clear(chat)
    if (history.length === 0) {
      chat.appendChild(
        h(
          'div',
          { class: 'stack-sm', style: { margin: 'auto 0', textAlign: 'center' } },
          h('div', { style: { fontSize: '2rem' } }, '🌙'),
          h('p', {}, d.companion.sub),
        ),
      )
    }
    history.forEach((m) => chat.appendChild(bubble(m)))
    chat.scrollTop = chat.scrollHeight
  }

  const submit = async (): Promise<void> => {
    const text = input.value.trim()
    if (!text) return
    haptic.tap()

    input.value = ''
    input.style.height = 'auto'
    send.disabled = true

    history.push({ role: 'user', text, at: Date.now() })
    repaint()

    const typing = h('div', { class: 'bubble companion faint' }, d.companion.thinking)
    chat.appendChild(typing)
    chat.scrollTop = chat.scrollHeight

    try {
      const reply = await respond(history.slice(0, -1), text, signal)
      typing.remove()
      history.push(reply)
      if (reply.safety) haptic.warn()
      repaint()
    } catch {
      typing.remove()
    }
  }

  append(root, [
    topbar(d.companion.title),
    offline && h('p', { class: 'faint', style: { marginBottom: '6px' } }, d.companion.offline),
    chat,
    h('div', { class: 'composer' }, input, send),
    h('p', { class: 'faint center', style: { paddingTop: '8px' } }, d.companion.disclaimer),
  ])

  repaint()
}

export function clearCompanionHistory(): void {
  history = []
}
